import { useEffect } from "react";
import styled from "styled-components";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { dateFormattedUTC, numberWithCommas } from "modules/functions";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";

import pointsIcon from "assets/img/icons/points-icon.svg";
import dollarIcon from "assets/img/icons/dollar-icon.svg";
import calendarIcon from "assets/img/icons/calendar-icon.svg";
import circledOneYellowIcon from "assets/img/icons/icons8-circled-1-yellow.svg";
import circledTwoGrenIcon from "assets/img/icons/icons8-circled-2-green.svg";
import circledThreeIndigoIcon from "assets/img/icons/icons8-circled-3-indigo.svg";
import clockIcon from "assets/img/icons/Circle-Clock-Icon.svg";
import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";

const ContestResults = styled.div``;

const ContestResultsBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const ContestData = styled.div`
  width: 100%;

  display: flex;
  gap: 35px 55px;

  @media (max-width: 400px) {
    flex-direction: column;
  }
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const DataIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DataIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const DataText = styled.div`
  white-space: nowrap;
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ResultsTable = styled.table`
  width: 100%;
  min-width: 350px;
`;

const ResultsTableHead = styled.thead``;

const ResultsLabel = styled.th`
  padding: 0 8px 12px 8px;

  font-size: 16px;
  font-weight: 500;
  text-align: left;
`;

const ResultsTableBody = styled.tbody``;

const ResultsRow = styled.tr``;

const ResultsCell = styled.td`
  padding: 4px 8px;
`;

const ResultsCellValue = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ResultsCellRankValue = styled.div`
  max-width: 30px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const ResultsCellValueIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const ResultsCellValueText = styled.div`
  white-space: nowrap;
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};
`;

const NoResults = styled.div`
  width: 100%;
  margin: 100px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};

  @media (max-width: ${globalConstants.switchMobile}) {
    margin: 60px 0;
  }
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div`
  text-align: center;
`;

export default ({ data, page, limit, paginate }) => {
  const navigate = useNavigate();

  let startDate;
  let endDate;
  let prizePool;

  if (data) {
    const { startsAt, endsAt } = data.contestResults;

    startDate = dateFormattedUTC(new Date(startsAt), false);
    endDate = dateFormattedUTC(new Date(endsAt), false);

    prizePool = data.contestResults.prizePool;
  }

  let resultsList = [];

  if (data) {
    resultsList = data.contestResults.results.map((result) => {
      return (
        <ResultsRow key={result.rank}>
          <ResultsCell>
            <ResultsCellRankValue>
              {result.rank === 1 && (
                <ResultsCellValueIcon>
                  <img src={circledOneYellowIcon} style={{ width: "100%" }} />
                </ResultsCellValueIcon>
              )}
              {result.rank === 2 && (
                <ResultsCellValueIcon>
                  <img src={circledTwoGrenIcon} style={{ width: "100%" }} />
                </ResultsCellValueIcon>
              )}
              {result.rank === 3 && (
                <ResultsCellValueIcon>
                  <img src={circledThreeIndigoIcon} style={{ width: "100%" }} />
                </ResultsCellValueIcon>
              )}
              {result.rank !== 1 && result.rank !== 2 && result.rank !== 3 && (
                <ResultsCellValueText>{result.rank}</ResultsCellValueText>
              )}
            </ResultsCellRankValue>
          </ResultsCell>
          <ResultsCell>
            <ResultsCellValue>
              <ResultsCellValueText>{result.username}</ResultsCellValueText>
            </ResultsCellValue>
          </ResultsCell>
          <ResultsCell>
            <ResultsCellValue>
              <ResultsCellValueIcon>
                <img src={pointsIcon} style={{ width: "100%" }} />
              </ResultsCellValueIcon>
              <ResultsCellValueText>
                {numberWithCommas(result.points)}
              </ResultsCellValueText>
            </ResultsCellValue>
          </ResultsCell>
          <ResultsCell>
            <ResultsCellValue>
              <ResultsCellValueIcon>
                <img src={dollarIcon} style={{ width: "100%" }} />
              </ResultsCellValueIcon>
              <ResultsCellValueText>{result.prize}</ResultsCellValueText>
            </ResultsCellValue>
          </ResultsCell>
        </ResultsRow>
      );
    });
  }

  // prepare pagination
  let showPagination = false;

  if (data?.contestResults.numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(data?.contestResults.numOfResults / limit);

  return (
    <ContestResults>
      {data && (
        <ContestResultsBox>
          {resultsList.length > 0 && (
            <>
              <ContestData>
                <Data>
                  <DataTitle>Term</DataTitle>
                  <DataRow>
                    <DataIconBox>
                      <DataIcon width="18px">
                        <img src={calendarIcon} style={{ width: "100%" }} />
                      </DataIcon>
                    </DataIconBox>
                    <DataText>
                      {startDate} - {endDate}
                    </DataText>
                  </DataRow>
                </Data>
                <Data>
                  <DataTitle>Prize Pool</DataTitle>
                  <DataRow>
                    <DataIconBox>
                      <DataIcon>
                        <img src={dollarIcon} style={{ width: "100%" }} />
                      </DataIcon>
                    </DataIconBox>
                    <DataText>{numberWithCommas(prizePool)} USD</DataText>
                  </DataRow>
                </Data>
              </ContestData>
              <Results>
                <TableWrap className="table-wrap">
                  <ResultsTable>
                    <ResultsTableHead>
                      <tr>
                        <ResultsLabel>Rank</ResultsLabel>
                        <ResultsLabel>Username</ResultsLabel>
                        <ResultsLabel>Points</ResultsLabel>
                        <ResultsLabel>Prize</ResultsLabel>
                      </tr>
                    </ResultsTableHead>
                    <ResultsTableBody>{resultsList}</ResultsTableBody>
                  </ResultsTable>
                </TableWrap>
                {showPagination && (
                  <Pagination>
                    {page > 1 && (
                      <ButtonRoundIcon
                        icon={leftIcon}
                        hoverIcon={leftWhiteIcon}
                        background={css.BRAND_YELLOW}
                        hoverBackground={css.BRAND_GREEN}
                        iconSize="16px"
                        buttonSize="32px"
                        onClick={() => paginate("previous")}
                      />
                    )}
                    {page < maxPage && (
                      <ButtonRoundIcon
                        icon={rightIcon}
                        hoverIcon={rightWhiteIcon}
                        background={css.BRAND_YELLOW}
                        hoverBackground={css.BRAND_GREEN}
                        iconSize="16px"
                        buttonSize="32px"
                        onClick={() => paginate("next")}
                      />
                    )}
                  </Pagination>
                )}
              </Results>
            </>
          )}
          {resultsList.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <img src={clockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              <NoResultsText>
                The results of the previous contest will be available later
                today.
              </NoResultsText>
            </NoResults>
          )}
        </ContestResultsBox>
      )}
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="700px" />
        </LoadingBox>
      )}
    </ContestResults>
  );
};
